import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { nanoid } from "nanoid";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  FileText,
  MoveRight,
  X,
} from "lucide-react";
import type {
  ReadingQuestion,
  ReadingQuestionType,
  PassagePage,
} from "@examing/types";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Checkbox } from "components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { RichTextEditor } from "modules/editor";
import { FillInBlanksEditor } from "./FillInBlanksEditor";
import { ShortAnswerFields, WordLimitField } from "./ShortAnswerFields";
import { QuestionField, QuestionValidationScope } from "./QuestionField";
import { resolvePassagePages } from "./passagePages";
import { cn } from "lib/utils";

const TYPES: Record<ReadingQuestionType, string> = {
  MULTIPLE_CHOICE: "single_choice",
  MULTIPLE_RESPONSE: "multiple_response",
  TRUE_FALSE: "true_false",
  SHORT_ANSWER: "short_answer",
  MATCHING: "matching",
  FILL_IN_BLANKS: "fill_in_blanks",
};

export interface PassageEditorProps {
  passageText: string;
  passageTitle?: string;
  subQuestions: ReadingQuestion[];
  pages?: PassagePage[];
  onPassageTextChange: (text: string) => void;
  onPassageTitleChange: (title: string) => void;
  onSubQuestionsChange: (questions: ReadingQuestion[]) => void;
  onPagesChange?: (
    pages: PassagePage[],
    consolidatedSubQuestions: ReadingQuestion[],
    mainPassageText: string,
    mainPassageTitle: string,
  ) => void;
}

function newQuestion(
  type: ReadingQuestionType,
  id = nanoid(8),
  pageIndex = 0,
  pageId = "",
): ReadingQuestion {
  return {
    id,
    type,
    questionText: "",
    points: 1,
    pageIndex,
    pageId,
    ...(type === "SHORT_ANSWER"
      ? { answerMode: "exact", acceptedAnswers: [] }
      : {}),
    ...(["MULTIPLE_CHOICE", "MULTIPLE_RESPONSE", "TRUE_FALSE"].includes(type)
      ? {
          options: (type === "TRUE_FALSE"
            ? ["True", "False"]
            : ["", "", "", ""]
          ).map((value) => ({ id: nanoid(6), value })),
          correctAnswer: type === "MULTIPLE_RESPONSE" ? [] : "",
        }
      : {}),
    ...(type === "MATCHING"
      ? {
          matchPairs: [
            { key: "", value: "" },
            { key: "", value: "" },
          ],
        }
      : {}),
  };
}

export function PassageEditor({
  passageText,
  passageTitle,
  subQuestions,
  pages: initialPages,
  onPassageTextChange,
  onPassageTitleChange,
  onSubQuestionsChange,
  onPagesChange,
}: PassageEditorProps) {
  const { t } = useTranslation();

  const [pages, setPages] = useState<PassagePage[]>(() =>
    resolvePassagePages({
      pages: initialPages,
      subQuestions,
      passageText,
      passageTitle,
      defaultTitle: (idx) =>
        idx === 0 ? passageTitle || "" : `${t("passage_label")} ${idx + 1}`,
      generateId: (idx) => `p-${idx + 1}-${nanoid(4)}`,
    }),
  );
  const [activePageIndex, setActivePageIndex] = useState(0);

  // Keep in sync if parent updates pages
  useEffect(() => {
    if (initialPages && initialPages.length > 0) {
      setPages(initialPages);
    }
  }, [initialPages]);

  // Ensure activePageIndex stays in bounds
  const safeActiveIndex = Math.min(
    Math.max(0, activePageIndex),
    Math.max(0, pages.length - 1),
  );
  const activePage = pages[safeActiveIndex] || pages[0] || {
    id: "p-1",
    passageTitle: "",
    passageText: "",
    subQuestions: [],
  };

  // Calculate question numbering offsets per page
  const pageQuestionOffsets = useMemo(() => {
    let offset = 0;
    return pages.map((p) => {
      const current = offset;
      offset += (p.subQuestions || []).length;
      return current;
    });
  }, [pages]);

  const totalQuestions = useMemo(
    () => pages.reduce((sum, p) => sum + (p.subQuestions?.length || 0), 0),
    [pages],
  );

  // Commit changes to local state & notify parent
  const commitPages = (nextPages: PassagePage[]) => {
    setPages(nextPages);

    const consolidated: ReadingQuestion[] = nextPages.flatMap(
      (page, pIdx) =>
        (page.subQuestions || []).map((q) => ({
          ...q,
          pageIndex: pIdx,
          pageId: page.id,
        })),
    );

    const mainPassageText = nextPages[0]?.passageText || "";
    const mainPassageTitle = nextPages[0]?.passageTitle || "";

    onPagesChange?.(
      nextPages,
      consolidated,
      mainPassageText,
      mainPassageTitle,
    );
    onSubQuestionsChange(consolidated);
    onPassageTextChange(mainPassageText);
    onPassageTitleChange(mainPassageTitle);
  };

  // Add a new page
  const handleAddPage = () => {
    const nextIndex = pages.length;
    const newPage: PassagePage = {
      id: `p-${nextIndex + 1}-${nanoid(4)}`,
      passageTitle: `${t("passage_label")} ${nextIndex + 1}`,
      passageText: "",
      subQuestions: [newQuestion("MULTIPLE_CHOICE", nanoid(8), nextIndex)],
    };
    const nextPages = [...pages, newPage];
    commitPages(nextPages);
    setActivePageIndex(nextIndex);
  };

  // Remove a page
  const handleRemovePage = (indexToRemove: number) => {
    if (pages.length <= 1) return;
    const nextPages = pages.filter((_, idx) => idx !== indexToRemove);
    commitPages(nextPages);
    if (activePageIndex >= nextPages.length) {
      setActivePageIndex(Math.max(0, nextPages.length - 1));
    }
  };

  // Update active page fields
  const handleUpdateActivePage = (patch: Partial<PassagePage>) => {
    const nextPages = pages.map((p, idx) =>
      idx === safeActiveIndex ? { ...p, ...patch } : p,
    );
    commitPages(nextPages);
  };

  // Subquestion helpers for active page
  const patchSubQuestion = (id: string, change: Partial<ReadingQuestion>) => {
    const nextPages = pages.map((p, idx) => {
      if (idx !== safeActiveIndex) return p;
      return {
        ...p,
        subQuestions: (p.subQuestions || []).map((q) =>
          q.id === id ? { ...q, ...change } : q,
        ),
      };
    });
    commitPages(nextPages);
  };

  const moveSubQuestion = (index: number, offset: number) => {
    const currentSubs = [...(activePage.subQuestions || [])];
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= currentSubs.length) return;
    [currentSubs[index], currentSubs[targetIndex]] = [
      currentSubs[targetIndex],
      currentSubs[index],
    ];
    handleUpdateActivePage({ subQuestions: currentSubs });
  };

  const removeSubQuestion = (id: string) => {
    const currentSubs = (activePage.subQuestions || []).filter(
      (q) => q.id !== id,
    );
    handleUpdateActivePage({ subQuestions: currentSubs });
  };

  const addSubQuestionToActivePage = () => {
    const newQ = newQuestion(
      "MULTIPLE_CHOICE",
      nanoid(8),
      safeActiveIndex,
      activePage.id,
    );
    handleUpdateActivePage({
      subQuestions: [...(activePage.subQuestions || []), newQ],
    });
  };

  const moveSubQuestionToPage = (
    questionId: string,
    targetPageIndex: number,
  ) => {
    if (targetPageIndex === safeActiveIndex) return;
    const qToMove = (activePage.subQuestions || []).find(
      (q) => q.id === questionId,
    );
    if (!qToMove) return;

    const nextPages = pages.map((p, idx) => {
      if (idx === safeActiveIndex) {
        return {
          ...p,
          subQuestions: (p.subQuestions || []).filter(
            (q) => q.id !== questionId,
          ),
        };
      }
      if (idx === targetPageIndex) {
        return {
          ...p,
          subQuestions: [
            ...(p.subQuestions || []),
            {
              ...qToMove,
              pageIndex: targetPageIndex,
              pageId: p.id,
            },
          ],
        };
      }
      return p;
    });

    commitPages(nextPages);
  };

  return (
    <div className="space-y-5">
      {/* Passage Pages Tab Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/80 pb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {pages.map((p, idx) => {
            const isActive = idx === safeActiveIndex;
            const qCount = (p.subQuestions || []).length;
            const startQ = (pageQuestionOffsets[idx] || 0) + 1;
            const endQ = (pageQuestionOffsets[idx] || 0) + qCount;
            const rangeLabel = qCount > 0 ? `Q${startQ}–${endQ}` : "0 Qs";
            const defaultTitle = `${t("passage_label")} ${idx + 1}`;
            const title = p.passageTitle?.trim() || defaultTitle;

            return (
              <div
                key={p.id}
                className={cn(
                  "group inline-flex items-center rounded-lg border text-xs font-medium transition-all",
                  isActive
                    ? "border-foreground bg-foreground text-background"
                    : "border-border/80 bg-card hover:bg-muted/70 text-foreground",
                )}
              >
                <button
                  type="button"
                  onClick={() => setActivePageIndex(idx)}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-left min-w-0"
                >
                  <span className="font-semibold truncate max-w-[90px] sm:max-w-[160px]" title={title}>{title}</span>
                  <span
                    className={cn(
                      "rounded-md px-1.5 py-0.5 text-[10px] font-mono font-semibold shrink-0",
                      isActive
                        ? "bg-background/20 text-background"
                        : "bg-muted text-muted-foreground border border-border/50",
                    )}
                  >
                    {rangeLabel}
                  </span>
                </button>

                {pages.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePage(idx);
                    }}
                    title={t("delete_passage_page")}
                    className={cn(
                      "mr-1.5 rounded p-0.5 transition-opacity shrink-0",
                      isActive
                        ? "text-background/80 hover:text-background hover:bg-background/20"
                        : "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                    )}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddPage}
            aria-label={t("add_passage_page")}
            title={t("add_passage_page")}
            className="h-8 px-2 sm:px-3 gap-1.5 text-xs font-medium border-dashed hover:border-foreground/30 shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t("add_passage")}</span>
          </Button>
        </div>

        {pages.length > 1 && (
          <span className="text-xs text-muted-foreground shrink-0 font-medium">
            {pages.length} {t("passages")} · {totalQuestions} {t("questions")}
          </span>
        )}
      </div>

      {/* Active Page Content */}
      <div className="space-y-4 rounded-xl border border-border/80 bg-card p-4 shadow-xs">
        {/* Passage Title */}
        <label className="block space-y-1 text-xs font-semibold text-foreground">
          <span>{t("passage_title")}</span>
          <Input
            value={activePage.passageTitle ?? ""}
            placeholder={`e.g. ${t("passage_label")} ${safeActiveIndex + 1}: The Origins of Agriculture`}
            onChange={(event) =>
              handleUpdateActivePage({ passageTitle: event.target.value })
            }
            className="text-sm"
          />
        </label>

        {/* Passage Text */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground">
            {t("reading_passage_text")}
          </p>
          <QuestionField name="passageText">
            <RichTextEditor
              value={activePage.passageText || ""}
              onChange={(passageText) =>
                handleUpdateActivePage({ passageText })
              }
              preset="question"
            />
          </QuestionField>
        </div>
      </div>

      {/* Subquestions on Active Page */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t("questions")}
            </h3>
            <span className="text-xs text-muted-foreground font-mono">
              ({(activePage.subQuestions || []).length} {t("questions")}
              {(activePage.subQuestions || []).length > 0
                ? ` · Q${(pageQuestionOffsets[safeActiveIndex] || 0) + 1}–${(pageQuestionOffsets[safeActiveIndex] || 0) + (activePage.subQuestions || []).length}`
                : ""}
              )
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addSubQuestionToActivePage}
            className="gap-1.5 text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("add_subquestion")}
          </Button>
        </div>

        {(activePage.subQuestions || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm font-medium text-foreground">
              {t("no_questions_in_page")}
            </p>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              {t("click_add_question_hint")}
            </p>
            <Button
              type="button"
              size="sm"
              onClick={addSubQuestionToActivePage}
              className="gap-1 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              {t("add_first_question")}
            </Button>
          </div>
        ) : (
          (activePage.subQuestions || []).map((q, localIndex) => {
            const globalNumber =
              (pageQuestionOffsets[safeActiveIndex] || 0) + localIndex + 1;
            const globalValidationIndex =
              (pageQuestionOffsets[safeActiveIndex] || 0) + localIndex;

            return (
              <QuestionValidationScope
                key={q.id}
                prefix={`subQuestions.${globalValidationIndex}`}
              >
                <div className="space-y-4 rounded-xl border border-border bg-card p-4.5 shadow-xs transition-shadow hover:shadow-sm">
                  {/* Question Header Controls */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
                      {globalNumber}
                    </span>

                    {/* Shadcn Select for Question Type */}
                    <QuestionField name="type">
                      <Select
                        value={q.type}
                        onValueChange={(nextType) => {
                          const fresh = newQuestion(
                            nextType as ReadingQuestionType,
                            q.id,
                            safeActiveIndex,
                            activePage.id,
                          );
                          patchSubQuestion(q.id, {
                            ...fresh,
                            questionText: q.questionText,
                            points: q.points,
                          });
                        }}
                      >
                        <SelectTrigger
                          aria-label={t("question_type")}
                          className="h-8 w-[130px] sm:w-[160px] text-xs font-medium"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TYPES).map(([typeKey, labelKey]) => (
                            <SelectItem key={typeKey} value={typeKey}>
                              {t(labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </QuestionField>

                    {/* Points Input */}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="hidden sm:inline">{t("points")}:</span>
                      <QuestionField name="points">
                        <Input
                          aria-label={`${t("points")} ${globalNumber}`}
                          type="number"
                          min={0.01}
                          step={0.01}
                          value={q.points}
                          onChange={(event) =>
                            patchSubQuestion(q.id, {
                              points: Number(event.target.value),
                            })
                          }
                          className="h-8 w-14 sm:w-16 text-xs font-semibold text-center"
                        />
                      </QuestionField>
                      <span className="sm:hidden text-[11px] font-medium">{t("pt")}</span>
                    </div>

                    {/* Move to another page selector if multiple pages exist */}
                    {pages.length > 1 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MoveRight className="h-3.5 w-3.5 shrink-0" />
                        <Select
                          value={String(safeActiveIndex)}
                          onValueChange={(targetIdxStr) =>
                            moveSubQuestionToPage(
                              q.id,
                              parseInt(targetIdxStr, 10),
                            )
                          }
                        >
                          <SelectTrigger className="h-8 w-[100px] sm:w-[130px] text-xs font-medium text-muted-foreground">
                            <SelectValue placeholder="Move..." />
                          </SelectTrigger>
                          <SelectContent>
                            {pages.map((targetPage, targetIdx) => (
                              <SelectItem
                                key={targetPage.id}
                                value={String(targetIdx)}
                                disabled={targetIdx === safeActiveIndex}
                              >
                                {targetPage.passageTitle?.trim()
                                  ? targetPage.passageTitle
                                  : `Passage ${targetIdx + 1}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Ordering & Delete Actions */}
                    <div className="ml-auto flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("reading_move_up")}
                        disabled={localIndex === 0}
                        onClick={() => moveSubQuestion(localIndex, -1)}
                        className="h-8 w-8 p-0"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("reading_move_down")}
                        disabled={
                          localIndex ===
                          (activePage.subQuestions?.length || 0) - 1
                        }
                        onClick={() => moveSubQuestion(localIndex, 1)}
                        className="h-8 w-8 p-0"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSubQuestion(q.id)}
                        className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {t("delete")}
                      </Button>
                    </div>
                  </div>

                  {/* Subquestion Prompt */}
                  {q.type === "FILL_IN_BLANKS" ? (
                    <>
                      <FillInBlanksEditor
                        prompt={q.questionText || q.prompt || ""}
                        blanks={(q.blanks ?? []).map((b) => ({
                          ...b,
                          acceptedAnswers: b.acceptedAnswers ?? [],
                        }))}
                        onChange={({ prompt: questionText, blanks }) =>
                          patchSubQuestion(q.id, { questionText, blanks })
                        }
                      />
                      <WordLimitField
                        value={q.maxWords}
                        onChange={(maxWords) =>
                          patchSubQuestion(q.id, { maxWords })
                        }
                      />
                    </>
                  ) : (
                    <QuestionField name="prompt">
                      <RichTextEditor
                        value={q.questionText || q.prompt || ""}
                        onChange={(questionText) =>
                          patchSubQuestion(q.id, { questionText })
                        }
                        preset="question"
                      />
                    </QuestionField>
                  )}

                  {/* Multiple Choice / Response / True False Options */}
                  {["MULTIPLE_CHOICE", "MULTIPLE_RESPONSE", "TRUE_FALSE"].includes(
                    q.type,
                  ) && (
                    <QuestionField name="correctAnswer">
                      <div className="space-y-2">
                        <QuestionField name="options">
                          <div className="space-y-2">
                            {q.type === "MULTIPLE_RESPONSE" ? (
                            <div className="space-y-2">
                              {(q.options ?? []).map((option, optionIndex) => {
                                const selected = Array.isArray(q.correctAnswer)
                                  ? q.correctAnswer.includes(option.id)
                                  : (q.options ?? []).find(
                                      (item) => item.id === option.id,
                                    )?.isCorrect === true;
                                const optionInputId = `editor-check-${q.id}-${option.id}`;
                                return (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-2"
                                  >
                                    <Checkbox
                                      id={optionInputId}
                                      aria-label={`${t("correct_answer")} ${optionIndex + 1}`}
                                      checked={selected}
                                      onCheckedChange={(checked) => {
                                        const current = Array.isArray(
                                          q.correctAnswer,
                                        )
                                          ? q.correctAnswer
                                          : (q.options ?? [])
                                              .filter((item) => item.isCorrect)
                                              .map((item) => item.id);
                                        const newAnswer = checked
                                          ? [...current, option.id]
                                          : current.filter(
                                              (id) => id !== option.id,
                                            );
                                        patchSubQuestion(q.id, {
                                          correctAnswer: newAnswer,
                                          options: q.options?.map((item) => ({
                                            ...item,
                                            isCorrect: newAnswer.includes(
                                              item.id,
                                            ),
                                          })),
                                        });
                                      }}
                                    />
                                    <input
                                      type="checkbox"
                                      aria-label={`${t("correct_answer")} ${optionIndex + 1}`}
                                      name={`key-${q.id}`}
                                      checked={selected}
                                      tabIndex={-1}
                                      aria-hidden="true"
                                      className="sr-only"
                                      onChange={(e) => {
                                        const current = Array.isArray(
                                          q.correctAnswer,
                                        )
                                          ? q.correctAnswer
                                          : (q.options ?? [])
                                              .filter((item) => item.isCorrect)
                                              .map((item) => item.id);
                                        const newAnswer = e.target.checked
                                          ? [...current, option.id]
                                          : current.filter(
                                              (id) => id !== option.id,
                                            );
                                        patchSubQuestion(q.id, {
                                          correctAnswer: newAnswer,
                                          options: q.options?.map((item) => ({
                                            ...item,
                                            isCorrect: newAnswer.includes(
                                              item.id,
                                            ),
                                          })),
                                        });
                                      }}
                                    />
                                    <QuestionField
                                      name={`options.${optionIndex}.value`}
                                      className="flex-1"
                                    >
                                      <Input
                                        aria-label={`${t("option")} ${optionIndex + 1}`}
                                        value={option.value ?? option.text ?? ""}
                                        onChange={(event) =>
                                          patchSubQuestion(q.id, {
                                            options: q.options?.map((item) =>
                                              item.id === option.id
                                                ? {
                                                    ...item,
                                                    value: event.target.value,
                                                    text: undefined,
                                                  }
                                                : item,
                                            ),
                                          })
                                        }
                                      />
                                    </QuestionField>
                                    {q.type !== "TRUE_FALSE" &&
                                      (q.options?.length ?? 0) > 2 && (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            patchSubQuestion(q.id, {
                                              options: q.options?.filter(
                                                (item) => item.id !== option.id,
                                              ),
                                              correctAnswer: Array.isArray(
                                                q.correctAnswer,
                                              )
                                                ? q.correctAnswer.filter(
                                                    (id) => id !== option.id,
                                                  )
                                                : q.correctAnswer === option.id
                                                  ? ""
                                                  : q.correctAnswer,
                                            })
                                          }
                                        >
                                          {t("delete")}
                                        </Button>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <RadioGroup
                              value={
                                typeof q.correctAnswer === "string"
                                  ? q.correctAnswer
                                  : (q.options ?? []).find(
                                      (item) => item.isCorrect,
                                    )?.id ?? ""
                              }
                              onValueChange={(selectedId) => {
                                patchSubQuestion(q.id, {
                                  correctAnswer: selectedId,
                                  options: q.options?.map((item) => ({
                                    ...item,
                                    isCorrect: item.id === selectedId,
                                  })),
                                });
                              }}
                              className="space-y-2"
                            >
                              {(q.options ?? []).map((option, optionIndex) => {
                                const selected =
                                  q.correctAnswer === option.id ||
                                  (!q.correctAnswer &&
                                    option.isCorrect === true);
                                const optionInputId = `editor-radio-${q.id}-${option.id}`;
                                return (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-2"
                                  >
                                    <RadioGroupItem
                                      id={optionInputId}
                                      value={option.id}
                                      aria-label={`${t("correct_answer")} ${optionIndex + 1}`}
                                    />
                                    <input
                                      type="radio"
                                      aria-label={`${t("correct_answer")} ${optionIndex + 1}`}
                                      name={`key-${q.id}`}
                                      value={option.id}
                                      checked={selected}
                                      tabIndex={-1}
                                      aria-hidden="true"
                                      className="sr-only"
                                      onChange={() => {
                                        patchSubQuestion(q.id, {
                                          correctAnswer: option.id,
                                          options: q.options?.map((item) => ({
                                            ...item,
                                            isCorrect: item.id === option.id,
                                          })),
                                        });
                                      }}
                                    />
                                    <QuestionField
                                      name={`options.${optionIndex}.value`}
                                      className="flex-1"
                                    >
                                      <Input
                                        aria-label={`${t("option")} ${optionIndex + 1}`}
                                        value={option.value ?? option.text ?? ""}
                                        onChange={(event) =>
                                          patchSubQuestion(q.id, {
                                            options: q.options?.map((item) =>
                                              item.id === option.id
                                                ? {
                                                    ...item,
                                                    value: event.target.value,
                                                    text: undefined,
                                                  }
                                                : item,
                                            ),
                                          })
                                        }
                                      />
                                    </QuestionField>
                                    {q.type !== "TRUE_FALSE" &&
                                      (q.options?.length ?? 0) > 2 && (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            patchSubQuestion(q.id, {
                                              options: q.options?.filter(
                                                (item) => item.id !== option.id,
                                              ),
                                              correctAnswer: Array.isArray(
                                                q.correctAnswer,
                                              )
                                                ? q.correctAnswer.filter(
                                                    (id) => id !== option.id,
                                                  )
                                                : q.correctAnswer === option.id
                                                  ? ""
                                                  : q.correctAnswer,
                                            })
                                          }
                                        >
                                          {t("delete")}
                                        </Button>
                                      )}
                                  </div>
                                );
                              })}
                            </RadioGroup>
                          )}
                          {q.type !== "TRUE_FALSE" && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                patchSubQuestion(q.id, {
                                  options: [
                                    ...(q.options ?? []),
                                    { id: nanoid(6), value: "" },
                                  ],
                                })
                              }
                            >
                              {t("add_option")}
                            </Button>
                          )}
                          </div>
                        </QuestionField>
                      </div>
                    </QuestionField>
                  )}

                  {/* Short Answer Fields */}
                  {q.type === "SHORT_ANSWER" && (
                    <ShortAnswerFields
                      data={q}
                      correctAnswer={q.correctAnswer}
                      onChange={(data, correctAnswer) =>
                        patchSubQuestion(q.id, {
                          ...data,
                          ...(correctAnswer !== undefined
                            ? { correctAnswer }
                            : {}),
                        })
                      }
                    />
                  )}

                  {/* Matching Question Fields */}
                  {q.type === "MATCHING" && (
                    <QuestionField name="matchPairs">
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">
                          {t("reading_matching_hint")}
                        </p>
                        {(q.matchPairs ?? []).map((pair, pairIndex) => (
                          <div key={pairIndex} className="flex gap-2">
                            <QuestionField
                              name={`matchPairs.${pairIndex}.key`}
                              className="flex-1"
                            >
                              <Input
                                aria-label={`${t("left_side")} ${pairIndex + 1}`}
                                value={pair.key}
                                onChange={(event) =>
                                  patchSubQuestion(q.id, {
                                    matchPairs: q.matchPairs?.map((item, i) =>
                                      i === pairIndex
                                        ? { ...item, key: event.target.value }
                                        : item,
                                    ),
                                  })
                                }
                              />
                            </QuestionField>
                            <QuestionField
                              name={`matchPairs.${pairIndex}.value`}
                              className="flex-1"
                            >
                              <Input
                                aria-label={`${t("correct_answer")} ${pairIndex + 1}`}
                                value={pair.value}
                                onChange={(event) =>
                                  patchSubQuestion(q.id, {
                                    matchPairs: q.matchPairs?.map((item, i) =>
                                      i === pairIndex
                                        ? { ...item, value: event.target.value }
                                        : item,
                                    ),
                                  })
                                }
                              />
                            </QuestionField>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                patchSubQuestion(q.id, {
                                  matchPairs: q.matchPairs?.filter(
                                    (_, i) => i !== pairIndex,
                                  ),
                                })
                              }
                            >
                              {t("delete")}
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            patchSubQuestion(q.id, {
                              matchPairs: [
                                ...(q.matchPairs ?? []),
                                { key: "", value: "" },
                              ],
                            })
                          }
                        >
                          {t("add_pair")}
                        </Button>
                        <label className="block space-y-1 text-sm">
                          <span>{t("reading_extra_options")}</span>
                          <textarea
                            rows={3}
                            value={(q.matchingOptions ?? []).join("\n")}
                            onChange={(event) =>
                              patchSubQuestion(q.id, {
                                matchingOptions: event.target.value.split("\n"),
                              })
                            }
                            className="w-full rounded-md border border-input bg-background p-2"
                          />
                        </label>
                      </div>
                    </QuestionField>
                  )}
                </div>
              </QuestionValidationScope>
            );
          })
        )}

        <QuestionField name="subQuestions">
          <Button
            type="button"
            variant="outline"
            onClick={addSubQuestionToActivePage}
            className="w-full gap-1.5 border-dashed py-5 text-xs font-semibold hover:border-foreground/30"
          >
            <Plus className="h-4 w-4" />
            {t("add_question")}
          </Button>
        </QuestionField>
      </div>
    </div>
  );
}

export default PassageEditor;
